import classNames from 'classnames';
import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';

import { useAnimatedList } from '@hooks/library/useAnimatedList';
import { useLibrarySwitcher } from '@hooks/library/useLibrarySwitcher';

import { ArrowIcon, ChevronUpIcon, PanelIcon } from '@icons/library/svg';

import { useDashboard } from '@components/Context/library/DashboardContext';
import { useGlobalState } from '@components/Context/library/GlobalStateContext';
import { Text, TypographyVariant } from '@components/library/atoms/Text';
import { Tooltip } from '@components/library/atoms/Tooltip';
import { AiAccuracyStatus } from '@components/library/molecules/AiAccuracyStatus';
import {
  Button,
  ButtonSize,
  ButtonType,
} from '@components/library/molecules/Button';
import { Dropdown } from '@components/library/molecules/Dropdown';
import { Input } from '@components/library/molecules/Input';

import type { LibraryToolbarProps } from './LibraryToolbar.types';

import styles from './LibraryToolbar.module.scss';

// Keep jump pills compact: shelf names longer than 20 chars are clipped with
// an ellipsis. The full name stays in the aria-label.
const truncateLabel = (name: string) =>
  name.length > 20 ? `${name.slice(0, 20)}…` : name;

const pillKey = <T extends { id: number }>(shelf: T) => String(shelf.id);

export function LibraryToolbar(props: LibraryToolbarProps): JSX.Element {
  const {
    shelves,
    matchedCount = null,
    search = '',
    onSearchChange,
    className,
  } = props;
  const {
    toggleSidebar,
    isSidebarCollapsed,
    toggleSidebarCollapsed,
    isOwner,
    isGuestMode,
    user,
    currentShelves,
  } = useGlobalState();
  const switcher = useLibrarySwitcher();
  // The active tag: while one gathers the library into a row, its pill is
  // the only one here, and a Clear beside it hands the shelves back.
  const { activeTagId, setActiveTagId } = useDashboard();
  const [selectedJumpShelfId, setSelectedJumpShelfId] = useState<number | null>(
    null,
  );

  // Horizontal scroller for the jump pills: when the row overflows, page
  // through it with the same arrows the shelves use.
  const jumpRef = useRef<HTMLDivElement>(null);
  const [canJumpLeft, setCanJumpLeft] = useState(false);
  const [canJumpRight, setCanJumpRight] = useState(false);

  const syncJumpScroll = useCallback(() => {
    const el = jumpRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanJumpLeft(scrollLeft > 1);
    setCanJumpRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = jumpRef.current;
    if (!el) return;
    syncJumpScroll();
    el.addEventListener('scroll', syncJumpScroll, { passive: true });
    const observer = new ResizeObserver(syncJumpScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', syncJumpScroll);
      observer.disconnect();
    };
  }, [syncJumpScroll, shelves.length]);

  const scrollJump = (direction: -1 | 1) => {
    const el = jumpRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * el.clientWidth * 0.8,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  };

  const { ref: pillsRef, entries: pillEntries } = useAnimatedList(
    shelves,
    pillKey,
    { collapse: 'width' },
  );

  const jumpOverflowing = canJumpLeft || canJumpRight;

  // Default to the first shelf as soon as the list lands; let the user
  // override by clicking, but keep their choice when the list identity
  // changes (e.g. after a surgical mutation).
  useEffect(() => {
    if (shelves.length === 0) {
      setSelectedJumpShelfId(null);
      return;
    }
    setSelectedJumpShelfId(prev =>
      prev != null && shelves.some(s => s.id === prev) ? prev : shelves[0].id,
    );
  }, [shelves]);

  // Where the page really starts: the global header and this sticky toolbar
  // together cover the top of the viewport, so a shelf brought to the very
  // top would sit under them with its header and half its objects hidden.
  // Measured live, since the toolbar's height changes with the breakpoint.
  const toolbarRef = useRef<HTMLDivElement>(null);
  const coveredTop = () =>
    toolbarRef.current?.getBoundingClientRect().bottom ?? 0;

  const handleJumpTo = (shelfId: number) => {
    // The pill list is the rendered list, so the target always exists.
    const el = document.getElementById(`shelf-${shelfId}`);
    if (!el) return;
    setSelectedJumpShelfId(shelfId);
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - coveredTop(),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  // Keep the highlighted pill honest while the page scrolls: the shelf
  // nearest the top of the viewport is the one the reader is on.
  useEffect(() => {
    if (shelves.length === 0 || typeof window === 'undefined') return;
    let raf = 0;
    const update = () => {
      raf = 0;
      let bestId: number | null = null;
      let bestDistance = Number.POSITIVE_INFINITY;
      const top = coveredTop();
      for (const shelf of shelves) {
        const el = document.getElementById(`shelf-${shelf.id}`);
        if (!el) continue;
        const distance = Math.abs(el.getBoundingClientRect().top - top);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestId = shelf.id;
        }
      }
      if (bestId != null) setSelectedJumpShelfId(bestId);
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [shelves]);

  const searchSummary =
    matchedCount == null
      ? ''
      : matchedCount === 0
        ? 'No matches'
        : `${matchedCount} ${matchedCount === 1 ? 'match' : 'matches'} on ${shelves.length} ${shelves.length === 1 ? 'shelf' : 'shelves'}`;

  return (
    <div ref={toolbarRef} className={classNames(styles.toolbar, className)}>
      {/* The heading is the library switcher itself: it names whose library
          this is and opens the list of every other one. Beside it, on phones,
          the only opener for the About panel — the panel is an off-screen
          drawer there. */}
      <div className={styles.identity}>
        <Dropdown
          variant="library"
          options={switcher.options}
          value={switcher.value}
          onChange={switcher.onChange}
          placeholder="Select library"
          ariaLabel="Switch library"
          className={styles.librarySwitcher}
        />
        <button
          type="button"
          className={styles.aboutButton}
          onClick={toggleSidebar}
          aria-label="About this library"
        >
          <PanelIcon />
        </button>
      </div>
      <div className={styles.jumpScrollerWrap}>
        {jumpOverflowing && (
          <Button
            className={classNames(styles.arrow, styles.arrowLeft)}
            onClick={() => scrollJump(-1)}
            type={ButtonType.Secondary}
            Icon={<ArrowIcon />}
            ariaLabel="Scroll shelves left"
            disabled={!canJumpLeft}
          />
        )}
        <div
          ref={jumpRef}
          className={classNames(styles.jumpButtons, styles.jumpScroll)}
        >
          {/* Pills come and go with the shelves and with the search: each
              arrives, leaves and slides along under the shared list motion. */}
          <div ref={pillsRef} className={styles.pillRow}>
            {pillEntries.map(({ item: shelf, leaving }) => {
              const isSelected = shelf.id === selectedJumpShelfId;
              return (
                <Tooltip
                  asChild
                  tooltipContent={
                    shelf.attributes.name.length > 20
                      ? shelf.attributes.name
                      : undefined
                  }
                  key={shelf.id}
                >
                  <span
                    key={shelf.id}
                    className={classNames(styles.pillSlot, {
                      [styles.pillLeaving]: leaving,
                    })}
                    data-flip-id={String(shelf.id)}
                    data-flip-leaving={leaving ? 'true' : undefined}
                    aria-hidden={leaving || undefined}
                  >
                    <Button
                      label={truncateLabel(shelf.attributes.name)}
                      ariaLabel={`Jump to ${shelf.attributes.name}`}
                      onClick={() => handleJumpTo(shelf.id)}
                      type={ButtonType.Secondary}
                      size={ButtonSize.Default}
                      className={classNames(styles.jumpButton, {
                        [styles.jumpSelected]: isSelected,
                      })}
                    />
                  </span>
                </Tooltip>
              );
            })}
          </div>
          {activeTagId != null && (
            <button
              type="button"
              className={styles.clearTag}
              onClick={() => setActiveTagId(null)}
              aria-label="Clear the tag filter"
            >
              Clear
            </button>
          )}
        </div>
        {jumpOverflowing && (
          <Button
            className={styles.arrow}
            onClick={() => scrollJump(1)}
            type={ButtonType.Secondary}
            Icon={<ArrowIcon />}
            ariaLabel="Scroll shelves right"
            disabled={!canJumpRight}
          />
        )}
      </div>

      <div className={styles.searchWrap}>
        <Input
          type="search"
          value={search}
          placeholder="Search everywhere"
          placeholderColor="#C4C4C4"
          onChange={e => onSearchChange?.(e.target.value)}
          onClear={() => onSearchChange?.('')}
          wrapperClassName={styles.search}
          ariaLabel="Search everywhere"
        />
        {/* The count sits in a line held from the start, so typing never
              pushes the toolbar around. */}
        <Text
          variant={TypographyVariant.TextSmall}
          className={styles.searchSummary}
          aria-live="polite"
        >
          {searchSummary}
        </Text>
      </div>

      {/* AI accuracy, where the Librarian opener stood: how much of what the
          engine reads is written into this library. The owner's alone, and
          not in a guest preview, since a visitor is told nothing of it. */}
      {isOwner && !isGuestMode && user && (
        <div className={styles.accuracySlot}>
          <AiAccuracyStatus shelves={currentShelves} />
        </div>
      )}

      {/* Desktop: folds the info panel away so the shelves take the width,
          and brings it back. One column at the toolbar's right edge, the
          height of both rows, beside the panel it controls; the arrow points
          the way the panel will go. The choice is per account and survives a
          refresh (GlobalState). Hidden where the panel is a drawer. */}
      <button
        type="button"
        className={classNames(styles.panelToggle, {
          [styles.panelToggleCollapsed]: isSidebarCollapsed,
        })}
        onClick={toggleSidebarCollapsed}
        aria-label={
          isSidebarCollapsed
            ? 'Show library panel, Control backslash'
            : 'Hide library panel, Control backslash'
        }
        aria-expanded={!isSidebarCollapsed}
        aria-controls="library-info-panel"
      >
        <ChevronUpIcon aria-hidden="true" />
      </button>
    </div>
  );
}
